import React, { useState } from 'react';
import { Button, Card, Divider, Modal, Space, Table, Tag, Tooltip, Typography, Grid, List, Empty, message } from 'antd';
import {
    FileTextOutlined, PrinterOutlined, EyeOutlined, CalendarOutlined,
    UserOutlined, DollarOutlined, FilePdfOutlined
} from '@ant-design/icons';
import { useVentasSesion } from '../../../hooks/useVentas';
import { pdf } from '@react-pdf/renderer';
import { FacturaFormal } from '../FacturaFormal';
import { generarTicketBluetooth } from '../../../utils/impresionBluetooth';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const SeccionListaVentas = ({ cajaActiva, setDatosImpresion, handlePrint, config }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { data: historialVentas = [], isLoading: cargandoHistorial } = useVentasSesion(cajaActiva?.id);
    const historialVentasSeguro = Array.isArray(historialVentas) ? historialVentas : [];

    const [modalVisible, setModalVisible] = useState(false);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);

    const formatearFecha = (fecha) => {
        return new Date(fecha).toLocaleString('es-CO', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const calcularMatematicasVenta = (venta) => {
        if (!venta) return { subtotal: 0, descuento: 0, total: 0 };
        let sumaDescuentos = 0;
        let sumaSubtotal = 0;

        (venta.items || []).forEach(item => {
            const precioBase = Number(item.precio_unitario) || 0;
            const cantidad = Number(item.cantidad) || 1;
            const porcentajeDescuento = Number(item.descuento) || 0;
            const subtotalItem = precioBase * cantidad;
            const ahorroItem = subtotalItem * (porcentajeDescuento / 100);
            sumaSubtotal += subtotalItem;
            sumaDescuentos += ahorroItem;
        });

        return {
            subtotal: sumaSubtotal,
            descuento: Math.round(sumaDescuentos),
            total: venta.total
        };
    };

    const abrirDetalles = (record) => {
        setVentaSeleccionada(record);
        setModalVisible(true);
    };

    const reimprimirTicketBluetooth = async (datosBase, infoNegocio, tipoPapel) => {
        try {
            if (!navigator.bluetooth) {
                return message.error("Tu dispositivo no soporta conexión Bluetooth directa desde el navegador.");
            }
    
            // Todos los UUIDs expuestos por SAT y DIGITALPOS
            const serviciosRequeridos = [
                '000018f0-0000-1000-8000-00805f9b34fb', // Impresora SAT (Cliente)
                '0000ff00-0000-1000-8000-00805f9b34fb', // DIGITALPOS (Canal Datos Estándar)
                '0000fee7-0000-1000-8000-00805f9b34fb', // DIGITALPOS (Canal Datos Secundario)
                '49535343-fe7d-41a3-8c53-0be4c0000001'  // DIGITALPOS SPP
            ];
    
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: serviciosRequeridos
            });
    
            const server = await device.gatt.connect();
    
            let characteristicTarget = null;
    
            // Recorremos los servicios
            for (const serviceUuid of serviciosRequeridos) {
                try {
                    const service = await server.getPrimaryService(serviceUuid);
                    const characteristics = await service.getCharacteristics();
    
                    // Tomamos la característica directamente de la lista
                    if (characteristics && characteristics.length > 0) {
                        // Si tiene características, tomamos la primera disponible o la que tenga permisos de escritura
                        characteristicTarget = characteristics.find(c => c.properties.write || c.properties.writeWithoutResponse) || characteristics[0];
                        if (characteristicTarget) {
                            //console.log(`Conectado al servicio ${serviceUuid} en característica ${characteristicTarget.uuid}`);
                            break;
                        }
                    }
                } catch (e) {
                    // Si el servicio no existe en el dispositivo conectado, continúa probando el siguiente
                }
            }
    
            if (!characteristicTarget) {
                device.gatt.disconnect();
                return message.error("No se encontró una característica válida en la impresora.");
            }
    
            // Generar payload
            const payload = generarTicketBluetooth(datosBase, infoNegocio, tipoPapel);
            const CHUNK_SIZE = 32; // Paquetes pequeños para prevenir congelamientos de buffer BLE
    
            for (let i = 0; i < payload.length; i += CHUNK_SIZE) {
                const chunk = payload.slice(i, i + CHUNK_SIZE);
                
                try {
                    await characteristicTarget.writeValue(chunk);
                } catch (errWrite) {
                    await characteristicTarget.writeValueWithoutResponse(chunk);
                }
                
                await new Promise(resolve => setTimeout(resolve, 50));
            }
    
            device.gatt.disconnect();
            message.success({ content: 'Reimpresión exitosa', key: 'imprimiendoBT' });
    
        } catch (error) {
            console.error("Error al reimprimir por Bluetooth:", error);
            message.error({ content: "Falló la impresión. Verifica la impresora.", key: 'imprimiendoBT' });
        }
    };

    const imprimirVentaHistorica = async (venta) => {
        const math = calcularMatematicasVenta(venta);
        const carritoMapeado = (venta.items || []).map(item => ({
            codigo_barras: item.codigo_barras || 'SERVICIO',
            nombre: item.nombre,
            numItems: item.cantidad || 1,
            precioVenta: Number(item.precio_unitario) || 0,
            descuento: Number(item.descuento) || 0
        }));

        const datosCompletos = {
            carrito: carritoMapeado,
            totales: { subtotal: math.subtotal, impuestos: 0, total: math.total, totalDescuento: math.descuento },
            ventaId: venta.id,
            numeroFactura: venta.numero_factura,
            fechaVenta: venta.fecha,
            clienteNombre: venta.cliente_nombre || 'CONSUMIDOR FINAL',
            clienteDocumento: venta.cliente_documento || '222222222222',
            clienteTelefono: venta.cliente_telefono || '31477870',
            clienteDireccion: venta.cliente_direccion,
            clienteEmail: venta.cliente_email || 'consufinal@gmail.com',
            cajero: `${venta.cajero_nombre || ''} ${venta.cajero_apellidos || ''}`,
            metodoPago: venta.metodo_pago,
            efectivoRecibido: venta.metodo_pago === 'Mixto' ? venta.monto_efectivo : venta.efectivo_recibido,
            cambioEntregado: venta.cambio_entregado,
            montoTransferencia: venta.monto_transferencia
        };

        const tipoImpresora = config?.impresora_tipo || config?.settings?.impresora_tipo || '80mm';
        const conexionImpresora = config?.settings?.conexion_impresora || 'sistema';

        const infoNegocio = {
            nombre: config.nombre,
            nombre_legal: config.nombre_legal,
            nit: config.identificacion_fiscal,
            direccion: config.direccion,
            telefono: config.telefono,
            logo_url: config.logo_url
        };

        if (tipoImpresora === 'carta_mitad') {
            const nuevaPestana = window.open('', '_blank');
            if (nuevaPestana) nuevaPestana.document.write('Generando documento oficial, por favor espere...');

            try {
                const blob = await pdf(<FacturaFormal {...datosCompletos} infoNegocio={infoNegocio} />).toBlob();
                const url = URL.createObjectURL(blob);

                if (nuevaPestana) {
                    nuevaPestana.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            } catch (error) {
                console.error("Error al construir el PDF: ", error);
                if (nuevaPestana) nuevaPestana.close();
            }

        } else if (conexionImpresora === 'bluetooth') {
            message.loading({ content: 'Conectando con impresora...', key: 'imprimiendoBT' });
            await reimprimirTicketBluetooth(datosCompletos, infoNegocio, tipoImpresora);
        } else {
            setDatosImpresion(datosCompletos);
            setTimeout(() => { handlePrint(); }, 300);
        }
    };

    const descargarPDFFormal = async (venta) => {
        const math = calcularMatematicasVenta(venta);
        const carritoMapeado = (venta.items || []).map(item => ({
            codigo_barras: item.codigo_barras, nombre: item.nombre, numItems: item.cantidad || 1, precioVenta: Number(item.precio_unitario) || 0, descuento: Number(item.descuento) || 0
        }));

        const datosCompletos = {
            carrito: carritoMapeado,
            totales: { subtotal: math.subtotal, impuestos: 0, total: math.total, totalDescuento: math.descuento },
            ventaId: venta.id, numeroFactura: venta.numero_factura, fechaVenta: venta.fecha,
            clienteNombre: venta.cliente_nombre || 'CONSUMIDOR FINAL', clienteDocumento: venta.cliente_documento || '222222222222',
            clienteTelefono: venta.cliente_telefono || '', clienteDireccion: venta.cliente_direccion || '', clienteEmail: venta.cliente_email || '',
            cajero: `${venta.cajero_nombre || ''} ${venta.cajero_apellidos || ''}`, metodoPago: venta.metodo_pago,
            efectivoRecibido: venta.metodo_pago === 'Mixto' ? venta.monto_efectivo : venta.efectivo_recibido,
            cambioEntregado: venta.cambio_entregado, montoTransferencia: venta.monto_transferencia
        };

        const infoNegocio = {
            nombre: config.nombre, nombre_legal: config.nombre_legal, nit: config.identificacion_fiscal,
            direccion: config.direccion, telefono: config.telefono, logo_url: config.logo_url
        };

        const nuevaPestana = window.open('', '_blank');
        if (nuevaPestana) nuevaPestana.document.write('Generando PDF, por favor espere...');

        try {
            const blob = await pdf(<FacturaFormal {...datosCompletos} infoNegocio={infoNegocio} />).toBlob();
            if (nuevaPestana) { nuevaPestana.location.href = URL.createObjectURL(blob); }
        } catch (error) {
            console.error(error);
            if (nuevaPestana) nuevaPestana.close();
        }
    };

    const columnas = [
        {
            title: 'Recibo', dataIndex: 'numero_factura',
            render: (t) => <Tag color="cyan" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>#{t || 'S/N'}</Tag>
        },
        { title: 'Fecha y Hora', dataIndex: 'fecha', render: (f) => formatearFecha(f) },
        {
            title: 'Cliente', dataIndex: 'cliente_nombre',
            render: (c) => <Text size="small">{c || 'Consumidor Final'}</Text>
        },
        {
            title: 'Método', dataIndex: 'metodo_pago',
            render: (m) => <Tag color={m === 'Credito' ? 'orange' : (m === 'Transferencia' ? 'blue' : (m === 'Mixto' ? 'purple' : 'green'))}>{m?.toUpperCase()}</Tag>
        },
        { title: 'Total', dataIndex: 'total', align: 'right', render: (t) => <Text strong>${Number(t).toLocaleString('es-CO')}</Text> },
        {
            title: 'Acciones', align: 'center',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Ver Detalles"><Button type="text" icon={<EyeOutlined />} onClick={() => abrirDetalles(record)} /></Tooltip>
                    <Tooltip title="Reimprimir"><Button type="text" icon={<PrinterOutlined />} style={{ color: '#22BABB', background: '#e0f2fe' }} onClick={() => imprimirVentaHistorica(record)} /></Tooltip>
                    <Tooltip title="Descargar PDF"><Button type="text" icon={<FilePdfOutlined />} style={{ color: '#d32f2f', background: '#fce4e4' }} onClick={() => descargarPDFFormal(record)} /></Tooltip>
                </Space>
            )
        }
    ];

    const mathModal = calcularMatematicasVenta(ventaSeleccionada);

    return (
        <div style={{ padding: isMobile ? '0' : '0 10px 10px 0', height: 'calc(100vh - 180px)', overflowY: 'auto' }}>
            <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 10, padding: '15px 0', marginBottom: '10px' }}>
                <Title level={4} style={{ margin: 0 }}>Ventas del Turno</Title>
            </div>

            {isMobile ? (
                <List
                    dataSource={historialVentasSeguro}
                    locale={{ emptyText: <Empty description="Sin ventas" /> }}
                    renderItem={(venta) => (
                        <Card
                            key={venta.id}
                            style={{ marginBottom: '12px', borderRadius: '12px', border: '1px solid #f1f5f9' }}
                            styles={{ body: { padding: '12px' } }}
                            actions={[
                                <Button type="text" icon={<EyeOutlined />} onClick={() => abrirDetalles(venta)}>Ver</Button>,
                                <Button type="text" icon={<PrinterOutlined />} style={{ color: '#22BABB' }} onClick={() => imprimirVentaHistorica(venta)}>Imprimir</Button>,
                                <Button type="text" icon={<FilePdfOutlined />} style={{ color: '#d32f2f', background: '#fce4e4' }} onClick={() => descargarPDFFormal(venta)}>PDF</Button>
                            ]}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <Tag color="cyan" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>#{venta.numero_factura}</Tag>
                                <Text strong style={{ fontSize: '16px' }}>${Number(venta.total).toLocaleString('es-CO')}</Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Space style={{ fontSize: '12px', color: '#64748b' }}><CalendarOutlined /> {formatearFecha(venta.fecha)}</Space>
                                <Space style={{ fontSize: '12px', color: '#64748b' }}><UserOutlined /> {venta.cliente_nombre || 'Consumidor Final'}</Space>
                                <Space style={{ fontSize: '12px' }}>
                                    <DollarOutlined style={{ color: '#64748b' }} />
                                    <Tag color={venta.metodo_pago === 'Credito' ? 'orange' : (venta.metodo_pago === 'Transferencia' ? 'blue' : 'green')} style={{ fontSize: '10px' }}>
                                        {venta.metodo_pago?.toUpperCase()}
                                    </Tag>
                                </Space>
                            </div>
                        </Card>
                    )}
                />
            ) : (
                <Card bordered={false} style={{ borderRadius: '16px' }}>
                    <Table columns={columnas} dataSource={historialVentasSeguro} loading={cargandoHistorial} rowKey="id" pagination={{ pageSize: 10 }} />
                </Card>
            )}

            <Modal
                title={<Space><FileTextOutlined style={{ color: '#22BABB' }} /><Text strong>DETALLE DE VENTA</Text></Space>}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={400}
                centered
                footer={[
                    <Button key="print" type="primary" block icon={<PrinterOutlined />} onClick={() => imprimirVentaHistorica(ventaSeleccionada)} style={{ background: '#22BABB', height: '45px', fontWeight: 'bold' }}>
                        REIMPRIMIR DOCUMENTO
                    </Button>
                ]}
                styles={{ body: { padding: 0 } }}
            >
                {ventaSeleccionada && (
                    <div style={{ padding: '25px', backgroundColor: '#fff', fontFamily: 'monospace', height: 'calc(100vh - 125px)' }}>
                        <style>{`
                .ticket-scroll::-webkit-scrollbar { width: 4px; }
                .ticket-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 4px; }
            `}</style>
                        <div style={{ textAlign: 'center', marginBottom: 15 }}>
                            <Title level={5} style={{ margin: 0 }}>{ventaSeleccionada.numero_factura ? `RECIBO #${ventaSeleccionada.numero_factura}` : 'TICKET DE VENTA'}</Title>
                            <Text style={{ fontSize: '11px' }}>{formatearFecha(ventaSeleccionada.fecha)}</Text>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ fontSize: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CAJERO:</Text><Text>{ventaSeleccionada.cajero_nombre?.toUpperCase()} {ventaSeleccionada.cajero_apellidos?.toUpperCase()}</Text></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CLIENTE:</Text><Text>{ventaSeleccionada.cliente_nombre?.toUpperCase() || 'CONSUMIDOR FINAL'}</Text></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CC/NIT:</Text><Text>{ventaSeleccionada.cliente_documento || '222222222222'}</Text></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>MEDIO DE PAGO:</Text><Text>{ventaSeleccionada.metodo_pago?.toUpperCase()}</Text></div>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div className="ticket-scroll" style={{ marginBottom: 15, maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                            {ventaSeleccionada.items?.map((item, i) => (
                                <div key={i} style={{ marginBottom: 8, fontSize: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ flex: 1 }}>{item.nombre?.toUpperCase()}</span>
                                        <Text strong>${Number(item.subtotal).toLocaleString('es-CO')}</Text>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#666' }}>{item.cantidad} UND x ${Number(item.precio_unitario).toLocaleString('es-CO')}</div>
                                </div>
                            ))}
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>SUBTOTAL:</Text><Text>${mathModal.subtotal.toLocaleString('es-CO')}</Text></div>
                            {mathModal.descuento > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16a34a' }}><Text>DCTO:</Text><Text>-${mathModal.descuento.toLocaleString('es-CO')}</Text></div>}

                            {/* Ajuste: Si es a crédito, el total se muestra normal, si no, se muestra gigante */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                fontSize: ventaSeleccionada.metodo_pago === 'Credito' ? '15px' : '18px',
                                fontWeight: 'bold', marginTop: 5
                            }}>
                                <span>TOTAL VENTA:</span><span>${Number(ventaSeleccionada.total).toLocaleString('es-CO')}</span>
                            </div>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '4px', fontSize: '11px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text strong>MÉTODO:</Text><Text>{ventaSeleccionada.metodo_pago?.toUpperCase()}</Text></div>
                            {ventaSeleccionada.metodo_pago === 'Efectivo' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>RECIBIDO:</Text><Text>${Number(ventaSeleccionada.efectivo_recibido || 0).toLocaleString('es-CO')}</Text></div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CAMBIO:</Text><Text strong>${Number(ventaSeleccionada.cambio_entregado || 0).toLocaleString('es-CO')}</Text></div>
                                </>
                            )}
                            {ventaSeleccionada.metodo_pago === 'Mixto' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>EFECTIVO:</Text><Text>${Number(ventaSeleccionada.monto_efectivo || 0).toLocaleString('es-CO')}</Text></div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>TRANSF.:</Text><Text>${Number(ventaSeleccionada.monto_transferencia || 0).toLocaleString('es-CO')}</Text></div>
                                </>
                            )}
                            {ventaSeleccionada.metodo_pago === 'Credito' && (
                                <div style={{ marginTop: '8px', borderTop: '1px dashed #cbd5e1', paddingTop: '8px' }}>
                                    {Number(ventaSeleccionada.efectivo_recibido) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Text>ABONO INICIAL:</Text>
                                            <Text>${Number(ventaSeleccionada.efectivo_recibido).toLocaleString('es-CO')}</Text>
                                        </div>
                                    )}
                                    {/* El saldo pendiente pasa a ser el protagonista visual */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cf1322', fontSize: '16px', background: '#fef2f2', padding: '4px', borderRadius: '4px' }}>
                                        <Text strong style={{ color: '#cf1322' }}>SALDO PENDIENTE:</Text>
                                        <Text strong style={{ color: '#cf1322' }}>
                                            ${Number((ventaSeleccionada.total || 0) - (ventaSeleccionada.efectivo_recibido || 0)).toLocaleString('es-CO')}
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'center', marginTop: 15, fontSize: '10px', color: '#999' }}>*** YUGONUB POS - GRACIAS ***</div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SeccionListaVentas;